import { AfterViewInit, Component, ElementRef, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { F4797Service } from './f-4797.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { safeEvalFormula, isFiniteEvalNumber } from '../../../shared/_utils/safe-eval-formula';

import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f-4797-ce',
    templateUrl: './f-4797.component.html',
    styleUrls: ['./f-4797.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F4797Component implements OnInit, AfterViewInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  @Output('open-section-details') openSectionDetails = new EventEmitter<any>();

  private readonly subs = new Subscription();

  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  
  filterParams: any = {};
  
  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  rowData: any = {};

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;
  isLocked = false;
  private lockObserver?: MutationObserver;

  // Component properties from AngularJS controller
  modalTitle: string = '';
  f4797Header: any = {};
  f4797List: any[] = [];
  f4797OriginalData: any[] = [];
  f4797FormData: any[] = [];
  f4797ColumnHeader: any[] = [];
  f4797FinalData: any[] = [];
  unAllocatedAmt: number = 0;
  section: any[] = [];
  classification: any = {
    'NA': 'NA',
    'Worthless': 'Worthless Stock',
    'Other': 'Other Flow Thru'
  };
  
  // Entity data
  entityList: any[] = [];
  leId: string = '';
  cdr_no: string = '';
  leType: string = '';
  
  // User settings
  userSettings: any;
  logged_in_user: string = '';
  scenario: string = '';
  glbl_issue_id: string = '';
  issue_short_desc: string = '';
  local_issue_id: string = '';
  cur_scenario_type: string = '';
  isValidScenario: boolean = false;
  isExamScenario: boolean = false;

  // Tab configuration
  tabs = [
    { id: 1, name: "Form Entry", active: true }
  ];
  activeTab = 1;

  constructor(
    private f4797Service: F4797Service,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private elementRef: ElementRef,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.init();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.isInitialized = false;
        this.applyWorkspaceContext();
        this.init();
        this.cdr.markForCheck();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.isInitialized = false;
        this.applyWorkspaceContext();
        this.init();
        this.cdr.markForCheck();
      })
    );
  }

  ngAfterViewInit(): void {
    this.detectLockState();
    setTimeout(() => this.detectLockState(), 60);
    setTimeout(() => this.detectLockState(), 300);
    this.lockObserver = new MutationObserver(() => this.detectLockState());
    this.lockObserver.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['class'] });
  }

  private detectLockState(): void {
    let el: HTMLElement | null = this.elementRef.nativeElement;
    while (el) {
      if (el.classList?.contains('modal-window-lock-class')) {
        if (!this.isLocked) {
          this.isLocked = true;
          this.cdr.markForCheck();
        }
        return;
      }
      el = el.parentElement;
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.lockObserver?.disconnect();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal) as GtwElementsBaseUrlsModel;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);
    this.appService = { USER_SETTINGS: header.userSettingsData || {}, SERVER_CONFIG: header.serverConfigData || {} };
    this.cdr.markForCheck();
  }

  // Flag to prevent multiple API calls
  private isInitialized = false;

  init() {
    try {
      // Prevent duplicate API calls
      if (this.isInitialized) {
        return;
      }

      // Check if ALL required inputs are available (including filterParams)
      if (!this.globalService || !this.GENERAL_CONFIG) {
        return;
      }
      
      if (!this.rowData || Object.keys(this.rowData).length === 0) {
        return;
      }
      
      if (!this.filterParams || Object.keys(this.filterParams).length === 0) {
        return;
      }

      // Mark as initialized BEFORE making API calls
      this.isInitialized = true;

      this.userSettings = this.appService.USER_SETTINGS;
      this.logged_in_user = this.userSettings?.user?.sso_id || '';
      
      this.setupEntityData();
      this.scenario = this.globalService?.globalParams?.scenario || '';
      this.glbl_issue_id = this.globalService?.globalParams?.issue_id || '';
      this.issue_short_desc = this.globalService?.globalParams?.issue_short_desc || '';
      this.isExamScenario = this.checkExamScenario();
      this.getF4797Data();
      this.getSectionTypes();
    } catch (e) {
      console.error('Error in F4797 init:', e);
      this.isInitialized = false; // Reset on error
    }
  }

  private setupEntityData() {
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.MARS || this.rowData.CDR_NO;
    this.leType = this.rowData.LE_TYPE;
    
    this.entityList = [
      {key: "Tax Year", value: this.rowData.TAX_YEAR},
      {key: "LEID", value: this.rowData.LEID},
      {key: "LE Name", value: this.rowData.LE_NAME}
    ];
  }

  checkExamScenario(): boolean {
    const scenarioTypeCode = this.globalService?.inputs?.tax_year?.selected?.scenario_type_code;
    if (scenarioTypeCode !== 'EXAM') {
      return false;
    }
    
    if (this.filterParams) {
      return this.filterParams.scenario_code?.trim().toUpperCase() === 'EXAM';
    }
    return false;
  }

  getF4797Data() {
    const jcdKey = this.rowData.JCD_KEY || 250;
    
    this.f4797Service.fetchF4797Details(
      this.GENERAL_CONFIG.base_url,
      this.rowData,
      this.logged_in_user,
      this.filterParams.scenario || this.scenario,
      jcdKey,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data && data.callSuccess === '1') {
          this.processFormData(data);
        } else {
          this._snackBar.open(data?.errorMessage || 'Error loading form data', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
        this.showFormLoader = false;
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error fetching F4797 data:', error);
        this._snackBar.open('Error loading form data', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.showFormLoader = false;
        this.cdr.markForCheck();
      }
    );
  }

  private processFormData(data: any) {
    this.f4797Header = data.jsonObject[0];
    this.modalTitle = this.f4797Header.ATTRIB_DESCRIPTION;
    this.f4797List = data.jsonObject.slice(1);
    
    for (let i = 0; i < this.f4797List.length; i++) {
      if (!this.isValidScenario && this.f4797List[i].T_SCENARIO_TYPE_CODE) {
        this.isValidScenario = this.checkEditScenario(
          this.f4797List[i].T_SCENARIO_TYPE_CODE,  
          this.f4797List[i].T_SCENARIO,  
          this.f4797List[i].ISSUE_KEY
        );
      }
    }
    
    const o = this.f4797List.reduce((a: any, b: any) => {
      a[b.LINE_ORDER] = a[b.LINE_ORDER] || [];
      a[b.LINE_ORDER].push(b);
      return a;
    }, {});
    
    const a = Object.keys(o).map((k: string) => {
      return {ROW_TYPE: o[k][0].ROW_TYPE, DELETE_ROWS: [], Line_data: o[k]};
    });
    
    this.f4797FormData = a;
    
    for (let i = 0; i < this.f4797FormData.length; i++) {
      if (this.f4797FormData[i].ROW_TYPE == 'CH') {
        if (this.f4797FormData[i].Line_data[0].OCCURRENCE != null) {
          const z = this.f4797FormData[i].Line_data.reduce((a: any, b: any) => {
            a[b.OCCURRENCE] = a[b.OCCURRENCE] || [];
            a[b.OCCURRENCE].push(b);
            return a;
          }, {});
          
          let y = Object.keys(z).map((k: string) => {
            return {
              Line_Type: "DATA_ROW", 
              Is_Multi: z[k][0].IS_MULTI, 
              Line_no: z[k][0].LINE_NO, 
              IS_EDITABLE: z[k][0].IS_EDITABLE, 
              IS_EDITTED: "N", 
              Trans_details_key: z[k][0].TRANS_DETAILS_KEY, 
              Occurrence: z[k][0].OCCURRENCE, 
              Line_data_occ: z[k]
            };
          });
          
          const m = _.cloneDeep(y);
          
          for (let x = 0; x < m.length; x++) {
            const d = (2 * x) + 1;
            y.splice(d, 0, m[x]);
            y[d].Line_Type = "DATA_COMMENTS";
            y[d].Line_data_occ = [y[2 * x].Line_data_occ[y[2 * x].Line_data_occ.length - 1]];
          }
          
          let c = _.cloneDeep(y[0]);
          c.Line_Type = "HEADER_ROW";
          c.Occurrence = "";
          c.Trans_details_key = "";
          c.IS_EDITABLE = "Y";
          for (let j = 0; j < c.Line_data_occ.length; j++) {
            c.Line_data_occ[j].ATTRIB_VALUE = c.Line_data_occ[j].ATTRIB_DESCRIPTION;
          }
          
          y.unshift(c, {
            Line_Type: "SUB_ROW", 
            Is_Multi: c.Is_Multi,
            Line_no: c.Line_no, 
            IS_EDITABLE: c.IS_EDITABLE,
            IS_EDITTED: "N",
            Trans_details_key: "",
            Occurrence: "",
            Line_data_occ: []
          });
          this.f4797FormData[i].Line_data = _.cloneDeep(y);
          
        } else {
          const z = this.f4797FormData[i].Line_data.reduce((a: any, b: any) => {
            a[b.LINE_NO] = a[b.LINE_NO] || [];
            a[b.LINE_NO].push(b);
            return a;
          }, {});
          
          const y = Object.keys(z).map((k: string) => {
            return {
              Line_Type: "HEADER_ROW", 
              Is_Multi: z[k][0].IS_MULTI, 
              Line_no: z[k][0].LINE_NO, 
              IS_EDITABLE: z[k][0].IS_EDITABLE, 
              IS_EDITTED: "N", 
              Trans_details_key: "", 
              Occurrence: "", 
              Line_data_occ: z[k]
            };
          });
          
          for (let j = 0; j < y[0].Line_data_occ.length; j++) {
            y[0].Line_data_occ[j].ATTRIB_VALUE = y[0].Line_data_occ[j].ATTRIB_DESCRIPTION;
          }
          
          y.push({
            Line_Type: "SUB_ROW", 
            Is_Multi: y[0].Is_Multi,
            Line_no: y[0].Line_no, 
            IS_EDITABLE: y[0].IS_EDITABLE,
            IS_EDITTED: "N",
            Trans_details_key: "",
            Occurrence: "",
            Line_data_occ: []
          });
          this.f4797FormData[i].Line_data = y;
        }
      } else if (this.f4797FormData[i].ROW_TYPE == 'DT') {
        for (let j = 0; j < this.f4797FormData[i].Line_data.length; j++) {
          if (this.f4797FormData[i].Line_data[j].IS_EDITABLE == 'Y' && 
              this.f4797FormData[i].Line_data[j].EDIT_TYPE == 'currency' && 
              this.f4797FormData[i].Line_data[j].DATA_TYPE == 'number') {
            this.f4797FormData[i].Line_data[j].ATTRIB_VALUE = parseInt(this.f4797FormData[i].Line_data[j].ATTRIB_VALUE);
          }
        }
      }
    }
    
    this.f4797OriginalData = _.cloneDeep(this.f4797FormData);
    this.getUnallocatedAmt();
    this.cdr.markForCheck();
  }

  getSectionTypes() {
    this.f4797Service.getSectionTypes(
      this.GENERAL_CONFIG.base_url,
      this.logged_in_user,
      this.rowData,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.section = [];
        for (let i = 0; i < data.jsonObject.length; i++) {
          this.section.push(data.jsonObject[i].VALUE);
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error fetching section types:', error);
        this.cdr.markForCheck();
      }
    );
  }

  checkEditScenario(s_type_code: string, t_scenario: string, t_issue: string): boolean {
    if (this.scenario == t_scenario && 
        ((s_type_code == 'EXAM' && this.glbl_issue_id == t_issue) || s_type_code != 'EXAM')) {
      return true;
    }
    return false;
  }

  getUnallocatedAmt() {
    for (let i = 0; i < this.f4797FormData.length; i++) {
      if (this.f4797FormData[i].ROW_TYPE == 'TH') {
        if (this.f4797FormData[i].Line_data[1].DESCRIPTION == 'Unallocated Amount' && 
            this.f4797FormData[i].Line_data[1].DATA_TYPE == 'number' && 
            this.f4797FormData[i].Line_data[1].ATTRIB_VALUE != null && 
            this.f4797FormData[i].Line_data[1].ATTRIB_VALUE != undefined) {
          this.unAllocatedAmt = parseInt(this.f4797FormData[i].Line_data[1].ATTRIB_VALUE);
        }
      }
    }
  }

  addNewRow(line_no: string) {
    let occurrance = 1;
    let trans_details_key = null;
    
    for (let i = 0; i < this.f4797FormData.length; i++) {
      if (this.f4797FormData[i].ROW_TYPE == 'CH') {
        if (null != this.f4797FormData[i].Line_data[this.f4797FormData[i].Line_data.length - 1].Occurrence && 
            this.f4797FormData[i].Line_data[this.f4797FormData[i].Line_data.length - 1].Occurrence != "" && 
            this.f4797FormData[i].Line_data[this.f4797FormData[i].Line_data.length - 1].Occurrence != undefined) {
          occurrance = this.f4797FormData[i].Line_data[this.f4797FormData[i].Line_data.length - 1].Occurrence + 1;
          trans_details_key = this.f4797FormData[i].Line_data[this.f4797FormData[i].Line_data.length - 1].Trans_details_key;
        }
        
        const new_row = _.cloneDeep(this.f4797FormData[i].Line_data[0]);
        new_row.Line_Type = "NEW_EDIT_ROW";
        new_row.Occurrence = occurrance;
        new_row.Trans_details_key = trans_details_key;
        new_row.IS_EDITTED = "Y";
        
        if (new_row.Line_no == line_no) {
          for (let j = 0; j < new_row.Line_data_occ.length; j++) {
            if (new_row.Line_data_occ[j].EDIT_TYPE == "custom_date_various" && 
                new_row.Line_data_occ[j].DATA_TYPE == "dateVarious") {
              // Convert current date to YYYY-MM-DD format for HTML date input (matches f-4562 pattern)
              new_row.Line_data_occ[j].ATTRIB_VALUE = this.convertToHtmlDateFormat(new Date());
            } else if (new_row.Line_data_occ[j].EDIT_TYPE == "currency" && 
                       new_row.Line_data_occ[j].DATA_TYPE == "number") {
              new_row.Line_data_occ[j].ATTRIB_VALUE = 0;
            } else if (new_row.Line_data_occ[j].EDIT_TYPE == "select" && 
                       new_row.Line_data_occ[j].DATA_TYPE == "string" &&
                       new_row.Line_data_occ[j].ATTRIB_DESCRIPTION == "Classification Type") {
              // Default Classification Type to 'NA' (matches AngularJS ng-init behavior)
              new_row.Line_data_occ[j].ATTRIB_VALUE = 'NA';
            } else if (new_row.Line_data_occ[j].EDIT_TYPE == "select" && 
                       new_row.Line_data_occ[j].DATA_TYPE == "string" &&
                       new_row.Line_data_occ[j].ATTRIB_DESCRIPTION == "Property Section") {
              // Default Property Section to first section (matches AngularJS ng-init behavior)
              if (this.section && this.section.length > 0) {
                new_row.Line_data_occ[j].ATTRIB_VALUE = this.section[0];
              }
            } else if (new_row.Line_data_occ[j].EDIT_TYPE == "label" && 
                       new_row.Line_data_occ[j].DATA_TYPE == "string") {
            } else {
              new_row.Line_data_occ[j].ATTRIB_VALUE = null;
            }
            new_row.Line_data_occ[j].OCCURRENCE = occurrance;
            new_row.Line_data_occ[j].TRANS_DETAILS_KEY = trans_details_key;
            new_row.Line_data_occ[j].IS_CHANGED_FLAG = "Y";
          }
          this.f4797FormData[i].Line_data.push(new_row);
        }
        
        const new_row_comments = _.cloneDeep(this.f4797FormData[i].Line_data[0]);
        new_row_comments.Line_Type = "NEW_EDIT_COMMENT_ROW";
        new_row_comments.Occurrence = occurrance;
        new_row_comments.Trans_details_key = trans_details_key;
        if (new_row_comments.Line_no == line_no) {
          new_row_comments.Line_data_occ = [new_row_comments.Line_data_occ[new_row_comments.Line_data_occ.length - 1]];
          new_row_comments.Line_data_occ[0].OCCURRENCE = occurrance;
          new_row_comments.Line_data_occ[0].ATTRIB_VALUE = null;
          new_row_comments.Line_data_occ[0].TRANS_DETAILS_KEY = trans_details_key;
          new_row_comments.Line_data_occ[0].IS_CHANGED_FLAG = "Y";
          this.f4797FormData[i].Line_data.push(new_row_comments);
        }
      }
    }
  }

  editExistingRecord(line_no: string, occurrence: number, index: number) {
    for (let i = 0; i < this.f4797FormData.length; i++) {
      if (this.f4797FormData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < this.f4797FormData[i].Line_data.length; j++) {
          if (j == index || j == index + 1) {
            if (this.f4797FormData[i].Line_data[j].Line_Type == "DATA_ROW" && 
                this.f4797FormData[i].Line_data[j].Line_no == line_no && 
                this.f4797FormData[i].Line_data[j].Occurrence == occurrence) {
              this.f4797FormData[i].Line_data[j].Line_Type = "NEW_EDIT_ROW";
              for (let l = 0; l < this.f4797FormData[i].Line_data[j].Line_data_occ.length; l++) {
                if (this.f4797FormData[i].Line_data[j].Line_data_occ[l].EDIT_TYPE == "currency" && 
                    this.f4797FormData[i].Line_data[j].Line_data_occ[l].DATA_TYPE == "number") {
                  this.f4797FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE = 
                    parseInt(this.f4797FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE);
                } else if (this.f4797FormData[i].Line_data[j].Line_data_occ[l].EDIT_TYPE == "custom_date_various" && 
                           this.f4797FormData[i].Line_data[j].Line_data_occ[l].DATA_TYPE == "dateVarious") {
                  if (this.f4797FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE != "VARIOUS") {
                    // Convert to YYYY-MM-DD format for HTML date input (matches f-4562 pattern)
                    this.f4797FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE = 
                      this.convertToHtmlDateFormat(this.f4797FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE);
                  }
                } else if (this.f4797FormData[i].Line_data[j].Line_data_occ[l].EDIT_TYPE == "checkbox" && 
                           this.f4797FormData[i].Line_data[j].Line_data_occ[l].DATA_TYPE == "string") {
                  // Initialize checkbox value to "N" if null or undefined (matches AngularJS behavior)
                  if (this.f4797FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE == null || 
                      this.f4797FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE == undefined) {
                    this.f4797FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE = "N";
                  }
                } else if (this.f4797FormData[i].Line_data[j].Line_data_occ[l].EDIT_TYPE == "select" && 
                           this.f4797FormData[i].Line_data[j].Line_data_occ[l].DATA_TYPE == "string" &&
                           this.f4797FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_DESCRIPTION == "Property Section") {
                  // Initialize Property Section to first section if value not in list (matches AngularJS ng-init)
                  if (this.section && this.section.length > 0) {
                    const currentVal = this.f4797FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE;
                    if (this.section.indexOf(currentVal) === -1) {
                      this.f4797FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE = this.section[0];
                    }
                  }
                }
              }
            } else if (this.f4797FormData[i].Line_data[j].Line_Type == "DATA_COMMENTS" && 
                       this.f4797FormData[i].Line_data[j].Line_no == line_no && 
                       this.f4797FormData[i].Line_data[j].Occurrence == occurrence) {
              this.f4797FormData[i].Line_data[j].Line_Type = "NEW_EDIT_COMMENT_ROW";
            }
          }
        }
      }
    }
  }

  removeExistingRecord(line_no: string, Occurrence: number, index: number) {
    for (let i = 0; i < this.f4797FormData.length; i++) {
      if (this.f4797FormData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < this.f4797FormData[i].Line_data.length; j++) {
          if (j == index) {
            if ((this.f4797FormData[i].Line_data[j].Line_Type == "DATA_ROW" || 
                 this.f4797FormData[i].Line_data[j].Line_Type == "NEW_EDIT_ROW") && 
                this.f4797FormData[i].Line_data[j].Line_no == line_no && 
                null != this.f4797FormData[i].Line_data[j].Occurrence && 
                this.f4797FormData[i].Line_data[j].Occurrence != "" && 
                this.f4797FormData[i].Line_data[j].Occurrence == Occurrence) {
              this.f4797FormData[i].DELETE_ROWS.push(this.f4797FormData[i].Line_data[j]);
              this.f4797FormData[i].Line_data.splice(j, 2);
            } else if ((this.f4797FormData[i].Line_data[j].Line_Type == "DATA_ROW" || 
                        this.f4797FormData[i].Line_data[j].Line_Type == "NEW_EDIT_ROW") && 
                       this.f4797FormData[i].Line_data[j].Line_no == line_no && 
                       (Occurrence == null || Occurrence == undefined)) {
              this.f4797FormData[i].Line_data.splice(j, 2);
            }
          }
        }
      }
    }
    this.calcFinalAmounts();
  }

  saveNewEdittedRecord(line_no: string, occurrence: number, index: number) {
    for (let i = 0; i < this.f4797FormData.length; i++) {
      if (this.f4797FormData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < this.f4797FormData[i].Line_data.length; j++) {
          if (j == index || j == index + 1) {
            if (this.f4797FormData[i].Line_data[j].Line_Type == "NEW_EDIT_ROW" && 
                this.f4797FormData[i].Line_data[j].Line_no == line_no && 
                this.f4797FormData[i].Line_data[j].Occurrence == occurrence) {
              this.f4797FormData[i].Line_data[j].Line_Type = "DATA_ROW";
            } else if (this.f4797FormData[i].Line_data[j].Line_Type == "NEW_EDIT_COMMENT_ROW" && 
                       this.f4797FormData[i].Line_data[j].Line_no == line_no && 
                       this.f4797FormData[i].Line_data[j].Occurrence == occurrence) {
              this.f4797FormData[i].Line_data[j].Line_Type = "DATA_COMMENTS";
            }
          }
          if (j == index) {
            for (let l = 0; l < this.f4797FormData[i].Line_data[j].Line_data_occ.length; l++) {
              if (this.f4797FormData[i].Line_data[j].Line_data_occ[l].IS_EDITABLE_CELL == "Y" && 
                  this.f4797FormData[i].Line_data[j].Line_data_occ[l].DATA_TYPE == "dateVarious" && 
                  this.f4797FormData[i].Line_data[j].Line_data_occ[l].EDIT_TYPE == "custom_date_various") {
                this.f4797FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE = 
                  this.getDateFormat(this.f4797FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE);
              } else if (this.f4797FormData[i].Line_data[j].Line_data_occ[l].IS_EDITABLE_CELL == "Y" && 
                         this.f4797FormData[i].Line_data[j].Line_data_occ[l].DATA_TYPE == "string" && 
                         this.f4797FormData[i].Line_data[j].Line_data_occ[l].EDIT_TYPE == "checkbox") {
                if (this.f4797FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE == null || 
                    this.f4797FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE == undefined) {
                  this.f4797FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE = "N";
                }
              }
            }
          }
        }
      }
    }
  }

  valueChanged(parent_index: number, occurrence: number, line_no: string, index: number) {
    for (let i = 0; i < this.f4797FormData.length; i++) {
      if (this.f4797FormData[i].ROW_TYPE == 'CH') {
        if (this.f4797FormData[i].Line_data[parent_index] != null && 
            this.f4797FormData[i].Line_data[parent_index] != undefined) {
          if (this.f4797FormData[i].Line_data[parent_index].Line_Type == "NEW_EDIT_COMMENT_ROW") {
            this.f4797FormData[i].Line_data[parent_index].Line_data_occ[0].IS_CHANGED_FLAG = "Y";
          } else {
            if (this.f4797FormData[i].Line_data[parent_index].Line_no == line_no && 
                this.f4797FormData[i].Line_data[parent_index].Occurrence == occurrence) {
              this.f4797FormData[i].Line_data[parent_index].Line_data_occ[index + 1].IS_CHANGED_FLAG = "Y";
              this.f4797FormData[i].Line_data[parent_index].Line_data_occ[index + 1].HAS_ERROR = "N";
            }
          }
        }
      }
      if (this.f4797FormData[i].ROW_TYPE == 'DT') {
        if (i == parent_index && 
            this.f4797FormData[i].Line_data[index] != null && 
            this.f4797FormData[i].Line_data[index] != undefined) {
          this.f4797FormData[i].Line_data[index].IS_CHANGED_FLAG = "Y";
        }
      }
    }
  }

  updateAmount(index: number, line_no: string) {
    for (let i = 0; i < this.f4797FormData.length; i++) {
      if (this.f4797FormData[i].ROW_TYPE == 'CH' && this.f4797FormData[i].Line_data[0].Line_no == line_no) {
        for (let j = 0; j < this.f4797FormData[i].Line_data[index].Line_data_occ.length; j++) {
          if (this.f4797FormData[i].Line_data[index].Line_data_occ[j].ATTRIB_CALC_FORMULA != null) {
            const line_data_values = this.f4797FormData[i].Line_data[index].Line_data_occ;
            this.f4797FormData[i].Line_data[index].Line_data_occ[j].ATTRIB_VALUE = 
              this.calcLineFormula(this.f4797FormData[i].Line_data[index].Line_data_occ[j].ATTRIB_CALC_FORMULA, line_data_values);
            this.f4797FormData[i].Line_data[index].Line_data_occ[j].IS_CHANGED_FLAG = "Y";
            this.f4797FormData[i].Line_data[index].Line_data_occ[j].HAS_ERROR = "N";
          }
        }
      }
    }
    this.calcFinalAmounts();
  }

  calcLineFormula(formula: string, line_data_values: any[]): number {
    let currentFormula = formula;
    const lineFormulaArray = formula.split(/[+-/\\*\\]/);
    
    for (let i = 0; i < lineFormulaArray.length; i++) {
      if (lineFormulaArray[i].indexOf("(") > -1 && lineFormulaArray[i].indexOf(")") > -1) {
        lineFormulaArray[i] = lineFormulaArray[i].replace(/"/g, "").replace(/'/g, "").replace(/\(|\)/g, "");
        const currentTabAtrribVal = line_data_values.filter((item: any) => 
          item.ATTRIB_SHORT_CODE === lineFormulaArray[i].trim()
        );
        const lineAmt = parseInt(currentTabAtrribVal[0]?.ATTRIB_VALUE || 0);
        if (parseInt(lineAmt.toString()) > 0) {
          currentFormula = currentFormula.replace("(" + lineFormulaArray[i].trim() + ")", lineAmt.toString());
        } else {
          currentFormula = currentFormula.replace("(" + lineFormulaArray[i].trim() + ")", "(" + lineAmt.toString() + ")");
        }
      }
    }
    
    try {
      const result = safeEvalFormula(currentFormula, null, 'f-4797');
      if (null != result && typeof result !== 'undefined' && isFiniteEvalNumber(result)) {
        return result;
      }
    } catch (e) {
      console.error('Error in calculation:', e);
    }
    return 0;
  }

  calcFinalAmounts() {
    for (let i = 0; i < this.f4797FormData.length; i++) {
      if (this.f4797FormData[i].ROW_TYPE == 'TH') {
        for (let j = 0; j < this.f4797FormData[i].Line_data.length; j++) {
          if (this.f4797FormData[i].Line_data[j].ATTRIB_CALC_FORMULA != null) {
            this.f4797FormData[i].Line_data[j].ATTRIB_VALUE = 
              this.calc_final_amt(this.f4797FormData[i].Line_data[j].ATTRIB_CALC_FORMULA);
            this.f4797FormData[i].Line_data[j].IS_CHANGED_FLAG = "Y";
          }
        }
      }
    }
    this.getUnallocatedAmt();
  }

  calc_final_amt(formula: string): number {
    let currentFormula = formula;
    const lineFormulaArray = formula.split(/[+-/\\*\\]/);
    
    for (let i = 0; i < lineFormulaArray.length; i++) {
      let lineAmt = 0;
      if (lineFormulaArray[i].indexOf("(") > -1 && lineFormulaArray[i].indexOf(")") > -1) {
        lineFormulaArray[i] = lineFormulaArray[i].replace(/"/g, "").replace(/'/g, "").replace(/\(|\)/g, "");
        for (let k = 0; k < this.f4797FormData.length; k++) {
          if (this.f4797FormData[k].ROW_TYPE == 'CH') {
            for (let j = 0; j < this.f4797FormData[k].Line_data.length; j++) {
              if (this.f4797FormData[k].Line_data[j].Line_Type == "DATA_ROW" || 
                  this.f4797FormData[k].Line_data[j].Line_Type == "NEW_EDIT_ROW") {
                const row_data = this.f4797FormData[k].Line_data[j].Line_data_occ;
                const currentTabAtrribVal = row_data.filter((item: any) => 
                  item.ATTRIB_SHORT_CODE === lineFormulaArray[i].trim()
                );
                if (null != currentTabAtrribVal && currentTabAtrribVal.length > 0) {
                  lineAmt = lineAmt + parseInt(currentTabAtrribVal[0].ATTRIB_VALUE || 0);
                }
              }
            }
          } else {
            const row_data = this.f4797FormData[k].Line_data;
            const currentTabAtrribVal = row_data.filter((item: any) => 
              item.ATTRIB_SHORT_CODE === lineFormulaArray[i].trim()
            );
            if (null != currentTabAtrribVal && currentTabAtrribVal.length > 0) {
              lineAmt = lineAmt + parseInt(currentTabAtrribVal[0].ATTRIB_VALUE || 0);
            }
          }
        }
        
        if (parseInt(lineAmt.toString()) > 0) {
          currentFormula = currentFormula.replace("(" + lineFormulaArray[i].trim() + ")", lineAmt.toString());
        } else {
          currentFormula = currentFormula.replace("(" + lineFormulaArray[i].trim() + ")", "(" + lineAmt.toString() + ")");
        }
      }
    }
    
    try {
      const result = safeEvalFormula(currentFormula, null, 'f-4797');
      if (null != result && typeof result !== 'undefined' && isFiniteEvalNumber(result)) {
        return result;
      }
    } catch (e) {
      console.error('Error in calculation:', e);
    }
    return 0;
  }

  getDateFormat(date_value: any): string {
    if (date_value == "VARIOUS") {
      return "VARIOUS";
    }
    
    if (date_value instanceof Date) {
      const month = (date_value.getMonth() > 8) ? (date_value.getMonth() + 1).toString() : ('0' + (date_value.getMonth() + 1));
      const day = (date_value.getDate() > 9) ? date_value.getDate().toString() : ('0' + date_value.getDate());
      return month + '/' + day + '/' + date_value.getFullYear();
    }
    
    // Handle string date in YYYY-MM-DD format (from HTML date input)
    if (typeof date_value === 'string' && date_value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const parts = date_value.split('-');
      return parts[1] + '/' + parts[2] + '/' + parts[0]; // MM/DD/YYYY
    }
    
    return date_value;
  }

  // Format date for display in DATA_ROW (converts YYYY-MM-DD to MM/DD/YYYY)
  formatDateForDisplay(date_value: any): string {
    if (!date_value) return '';
    if (date_value === 'VARIOUS') return 'VARIOUS';
    
    // Handle YYYY-MM-DD format
    if (typeof date_value === 'string' && date_value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const parts = date_value.split('-');
      return parts[1] + '/' + parts[2] + '/' + parts[0]; // MM/DD/YYYY
    }
    
    // If already in MM/DD/YYYY format or other format, return as-is
    return date_value;
  }

  convertToDateInputFormat(date: any): string {
    if (!date || date === "VARIOUS") return '';
    
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    
    return d.getFullYear().toString() + '-' + 
           (d.getMonth() + 1).toString().padStart(2, '0') + '-' + 
           d.getDate().toString().padStart(2, '0');
  }

  // Helper: Get difference in days between two dates (matches AngularJS getDiffDates)
  getDiffDates(dateAcquired: any, dateSold: any): number {
    let d1: Date;
    let d2: Date;
    
    if (typeof dateAcquired === 'string') {
      d1 = this.convertToDate(dateAcquired);
      d2 = this.convertToDate(dateSold);
    } else {
      d1 = dateAcquired;
      d2 = dateSold;
    }
    
    return (d2.getTime() - d1.getTime()) / (1000 * 3600 * 24);
  }

  // Helper: Convert string to Date (matches AngularJS convertToDate)
  convertToDate(str: string): Date {
    // Handle YYYY-MM-DD format
    if (str.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const parts = str.split('-');
      return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    }
    // Handle MM/DD/YYYY format
    const dateArr = str.split("/");
    return new Date(parseInt(dateArr[2]), parseInt(dateArr[0]) - 1, parseInt(dateArr[1]));
  }

  // Helper: Extract year from date string (handles YYYY-MM-DD and MM/DD/YYYY formats)
  getYearFromDateString(dateStr: string): number | null {
    if (!dateStr) return null;
    
    // YYYY-MM-DD format (from HTML date input)
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return parseInt(dateStr.substring(0, 4));
    }
    
    // MM/DD/YYYY format (from AngularJS or stored data)
    const year_match = dateStr.match(/\d{4}$/);
    if (year_match) {
      return parseInt(year_match[0]);
    }
    
    return null;
  }

  // Enter section details modal (matches AngularJS enterSectionDetails)
  enterSectionDetails(attrib_short_code: string, mainIndex: number, parentIndex: number, attribValue: string, occurrence: number) {
    const short_desc = attrib_short_code.substring(0, attrib_short_code.lastIndexOf("_"));
    
    if (short_desc != null && short_desc == "SECTION_TYPE" && attribValue !== "1245") {
      // Build sourceRow object matching AngularJS
      const sourceRow: any = {
        sectionVal: attribValue,
        occurrence: occurrence,
        tax_year: this.globalService?.globalParams?.tax_year || this.rowData.TAX_YEAR,
        scenario: this.globalService?.globalParams?.scenario || ''
      };
      
      // Get the dataRow from f4797FormData
      const dataRow = this.f4797FormData[mainIndex]?.Line_data[parentIndex];
      
      // Emit event to AngularJS parent to open the modal
      this.openSectionDetails.emit({
        dataRow: dataRow,
        rowData: this.rowData,
        sourceRow: sourceRow
      });
    }
  }

  validateFormValues(): number {
    let returnValue = 0;
    const tax_year = this.globalService?.globalParams?.tax_year || this.rowData.TAX_YEAR;
    
    for (let i = 0; i < this.f4797FormData.length; i++) {
      if (this.f4797FormData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < this.f4797FormData[i].Line_data.length; j++) {
          const line_no = this.f4797FormData[i].Line_data[j].Line_no;
          if (this.f4797FormData[i].Line_data[j].Line_Type == "DATA_ROW" || 
              this.f4797FormData[i].Line_data[j].Line_Type == "NEW_EDIT_ROW") {
            
            const property_desc_index = this.f4797FormData[i].Line_data[j].Line_data_occ.findIndex((x: any) => 
              x.ATTRIB_SHORT_CODE == "PROPERTY_DESC_" + line_no
            );
            const date_acquired_index = this.f4797FormData[i].Line_data[j].Line_data_occ.findIndex((x: any) => 
              x.ATTRIB_SHORT_CODE == "DATE_ACQ_" + line_no
            );
            const date_sold_index = this.f4797FormData[i].Line_data[j].Line_data_occ.findIndex((x: any) => 
              x.ATTRIB_SHORT_CODE == "DATE_SOLD_" + line_no
            );
            const cost_basis_index = this.f4797FormData[i].Line_data[j].Line_data_occ.findIndex((x: any) => 
              x.ATTRIB_SHORT_CODE == "COST_BASIS_" + line_no
            );
            const depreciation_index = this.f4797FormData[i].Line_data[j].Line_data_occ.findIndex((x: any) => 
              x.ATTRIB_SHORT_CODE == "DEPRECIATION_ALLOWED_" + line_no
            );
            const gain_loss_index = this.f4797FormData[i].Line_data[j].Line_data_occ.findIndex((x: any) => 
              x.ATTRIB_SHORT_CODE == "GAIN_LOSS_" + line_no
            );
            const classification_type_index = this.f4797FormData[i].Line_data[j].Line_data_occ.findIndex((x: any) => 
              x.ATTRIB_SHORT_CODE == "FLOW_TYPE_" + line_no
            );
            const worthless_flag_index = this.f4797FormData[i].Line_data[j].Line_data_occ.findIndex((x: any) => 
              x.ATTRIB_SHORT_CODE == "WORTHLESS_FLAG_" + line_no
            );
            
            // Property description validation (matches AngularJS - no index check)
            const prop_val = this.f4797FormData[i].Line_data[j].Line_data_occ[property_desc_index]?.ATTRIB_VALUE;
            if (prop_val == null || prop_val == undefined || prop_val.length <= 0) {
              this.f4797FormData[i].Line_data[j].Line_data_occ[property_desc_index].HAS_ERROR = "Y";
              this.f4797FormData[i].Line_data[j].Line_data_occ[property_desc_index].ATTRIB_VALUE_ERROR = "Enter Description of Property.";
              returnValue = 1;
            }
            
            // Date acquired validation (handles both YYYY-MM-DD and MM/DD/YYYY formats)
            const date_acq_val = this.f4797FormData[i].Line_data[j].Line_data_occ[date_acquired_index]?.ATTRIB_VALUE;
            if (date_acq_val != "VARIOUS") {
              if (typeof date_acq_val === 'object' && date_acq_val && date_acq_val.getYear() > (parseInt(tax_year) - 1900)) {
                this.f4797FormData[i].Line_data[j].Line_data_occ[date_acquired_index].HAS_ERROR = "Y";
                this.f4797FormData[i].Line_data[j].Line_data_occ[date_acquired_index].ATTRIB_VALUE_ERROR = 
                  "The Date Acquired year has to be current tax year or prior year.";
                returnValue = 1;
              } else if (typeof date_acq_val === 'string') {
                // Extract year from both YYYY-MM-DD and MM/DD/YYYY formats
                const dateYear = this.getYearFromDateString(date_acq_val);
                if (dateYear && dateYear > parseInt(tax_year)) {
                  this.f4797FormData[i].Line_data[j].Line_data_occ[date_acquired_index].HAS_ERROR = "Y";
                  this.f4797FormData[i].Line_data[j].Line_data_occ[date_acquired_index].ATTRIB_VALUE_ERROR = 
                    "The Date Acquired year has to be current tax year or prior year.";
                  returnValue = 1;
                }
              }
            }
            
            // Date sold validation (handles both YYYY-MM-DD and MM/DD/YYYY formats)
            const date_sold_val = this.f4797FormData[i].Line_data[j].Line_data_occ[date_sold_index]?.ATTRIB_VALUE;
            if (date_sold_val != "VARIOUS") {
              if (typeof date_sold_val === 'object' && date_sold_val && date_sold_val.getYear() > (parseInt(tax_year) - 1900)) {
                this.f4797FormData[i].Line_data[j].Line_data_occ[date_sold_index].HAS_ERROR = "Y";
                this.f4797FormData[i].Line_data[j].Line_data_occ[date_sold_index].ATTRIB_VALUE_ERROR = 
                  "The Date Sold year has to be same as tax year.";
                returnValue = 1;
              } else if (typeof date_sold_val === 'string') {
                // Extract year from both YYYY-MM-DD and MM/DD/YYYY formats
                const dateYear = this.getYearFromDateString(date_sold_val);
                if (dateYear && dateYear > parseInt(tax_year)) {
                  this.f4797FormData[i].Line_data[j].Line_data_occ[date_sold_index].HAS_ERROR = "Y";
                  this.f4797FormData[i].Line_data[j].Line_data_occ[date_sold_index].ATTRIB_VALUE_ERROR = 
                    "The Date Sold year has to be same as tax year.";
                  returnValue = 1;
                }
              }
            }
            
            // Date range validation (date acquired vs date sold) - matches AngularJS
            if (date_sold_index !== -1 && date_acquired_index !== -1) {
              const date_sold_val = this.f4797FormData[i].Line_data[j].Line_data_occ[date_sold_index].ATTRIB_VALUE;
              const date_acq_val = this.f4797FormData[i].Line_data[j].Line_data_occ[date_acquired_index].ATTRIB_VALUE;
              
              if (date_sold_val != "VARIOUS" && date_acq_val != "VARIOUS") {
                const date_acquired = Date.parse(date_acq_val);
                const date_sold = Date.parse(date_sold_val);
                
                if (date_acquired > date_sold) {
                  this.f4797FormData[i].Line_data[j].Line_data_occ[date_acquired_index].HAS_ERROR = "Y";
                  this.f4797FormData[i].Line_data[j].Line_data_occ[date_acquired_index].ATTRIB_VALUE_ERROR = 
                    "The Date Acquired should be prior to Date Sold";
                  returnValue = 1;
                }
                
                const diff_in_dates = this.getDiffDates(date_acq_val, date_sold_val);
                
                // Line 10 validation: Date range must be shorter than one year for Part II
                if (diff_in_dates > 365 && line_no == "10") {
                  if (worthless_flag_index !== -1) {
                    const worthless_flag_val = this.f4797FormData[i].Line_data[j].Line_data_occ[worthless_flag_index].ATTRIB_VALUE;
                    if (worthless_flag_val == "N") {
                      this.f4797FormData[i].Line_data[j].Line_data_occ[date_sold_index].HAS_ERROR = "Y";
                      this.f4797FormData[i].Line_data[j].Line_data_occ[date_sold_index].ATTRIB_VALUE_ERROR = 
                        "Date Range must be shorter than one year for Part II.";
                      returnValue = 1;
                    } else {
                      if (!confirm("The Worthless Stock record was not in Date range. Please confirm to save the changes.")) {
                        returnValue = 1;
                      }
                    }
                  }
                  if (classification_type_index !== -1) {
                    const classification_val = this.f4797FormData[i].Line_data[j].Line_data_occ[classification_type_index].ATTRIB_VALUE;
                    if (classification_val !== "Worthless") {
                      this.f4797FormData[i].Line_data[j].Line_data_occ[date_sold_index].HAS_ERROR = "Y";
                      this.f4797FormData[i].Line_data[j].Line_data_occ[date_sold_index].ATTRIB_VALUE_ERROR = 
                        "Date Range must be shorter than one year for Part II.";
                      returnValue = 1;
                    } else {
                      if (!confirm("The Worthless Stock record was not in Date range. Please confirm to save the changes.")) {
                        returnValue = 1;
                      }
                    }
                  }
                } else if ((diff_in_dates <= 365 && line_no == "2") || (diff_in_dates <= 365 && line_no == "19")) {
                  // Lines 2 and 19: Date range must be longer than one year
                  this.f4797FormData[i].Line_data[j].Line_data_occ[date_sold_index].HAS_ERROR = "Y";
                  this.f4797FormData[i].Line_data[j].Line_data_occ[date_sold_index].ATTRIB_VALUE_ERROR = 
                    "Date Range must be longer than one year. If otherwise, please enter this data in Part II.";
                  returnValue = 1;
                }
              }
            }
            
            // Cost basis validation (matches AngularJS exactly)
            const cost_basis_val = this.f4797FormData[i].Line_data[j].Line_data_occ[cost_basis_index]?.ATTRIB_VALUE;
            if (cost_basis_val == 0 && j + 1 < this.f4797FormData[i].Line_data.length) {
              const nextRow = this.f4797FormData[i].Line_data[j + 1].Line_data_occ[0];
              // Match AngularJS: check hasOwnProperty OR (hasOwnProperty AND value is null/undefined)
              if (!nextRow.hasOwnProperty("ATTRIB_VALUE") ||
                  (nextRow.hasOwnProperty("ATTRIB_VALUE") && 
                   (nextRow.ATTRIB_VALUE == null || nextRow.ATTRIB_VALUE == undefined))) {
                this.f4797FormData[i].Line_data[j].Line_data_occ[cost_basis_index].HAS_ERROR = "Y";
                this.f4797FormData[i].Line_data[j].Line_data_occ[cost_basis_index].ATTRIB_VALUE_ERROR = 
                  "When Cost or Other Basis amount is 0, an explanation comment must be entered!";
                returnValue = 1;
              }
            }
            if (cost_basis_val < 0) {
              this.f4797FormData[i].Line_data[j].Line_data_occ[cost_basis_index].HAS_ERROR = "Y";
              this.f4797FormData[i].Line_data[j].Line_data_occ[cost_basis_index].ATTRIB_VALUE_ERROR = 
                "As the Amounts are in Increase/Decrease format, Cost Basis should not be less than zero!";
              returnValue = 1;
            }
            
            // Line 2 specific validation (matches AngularJS - no index check)
            if (line_no == "2") {
              const depreciation_val = this.f4797FormData[i].Line_data[j].Line_data_occ[depreciation_index]?.ATTRIB_VALUE;
              const gain_loss_val = this.f4797FormData[i].Line_data[j].Line_data_occ[gain_loss_index]?.ATTRIB_VALUE;
              if (depreciation_val != 0 && gain_loss_val > 0) {
                this.f4797FormData[i].Line_data[j].Line_data_occ[gain_loss_index].HAS_ERROR = "Y";
                this.f4797FormData[i].Line_data[j].Line_data_occ[gain_loss_index].ATTRIB_VALUE_ERROR = 
                  "Please enter Gain Transactions with depreciation in Part III.";
                returnValue = 1;
              }
            }
            
            // Line 19 specific validation (matches AngularJS - no index check)
            if (line_no == "19") {
              const gain_loss_val = this.f4797FormData[i].Line_data[j].Line_data_occ[gain_loss_index]?.ATTRIB_VALUE;
              if (gain_loss_val < 0) {
                this.f4797FormData[i].Line_data[j].Line_data_occ[gain_loss_index].HAS_ERROR = "Y";
                this.f4797FormData[i].Line_data[j].Line_data_occ[gain_loss_index].ATTRIB_VALUE_ERROR = 
                  "Please enter Loss Transactions in Part I.";
                returnValue = 1;
              }
            }
          }
        }
      } else if (this.f4797FormData[i].ROW_TYPE == 'DT') {
        for (let j = 0; j < this.f4797FormData[i].Line_data.length; j++) {
          const line_no = this.f4797FormData[i].Line_data[j].LINE_NO;
          const gain_loss_index = this.f4797FormData[i].Line_data.findIndex((x: any) => 
            x.ATTRIB_SHORT_CODE == "GAIN_LOSS_" + line_no
          );
          if (line_no == "8" && gain_loss_index !== -1 && this.f4797FormData[i].Line_data[gain_loss_index].ATTRIB_VALUE < 0) {
            this.f4797FormData[i].Line_data[j].HAS_ERROR = "Y";
            this.f4797FormData[i].Line_data[j].ATTRIB_VALUE_ERROR = "Please enter a valid amount for Line 8 in Part I.";
            returnValue = 1;
          }
        }
      }
    }
    return returnValue;
  }

  // Update form data values before saving (convert dates, set checkbox defaults)
  updateFormDataValues() {
    for (let i = 0; i < this.f4797FinalData.length; i++) {
      if (this.f4797FinalData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < this.f4797FinalData[i].Line_data.length; j++) {
          const lineType = this.f4797FinalData[i].Line_data[j].Line_Type;
          if (lineType == "NEW_EDIT_ROW" || lineType == "DATA_ROW") {
            for (let l = 0; l < this.f4797FinalData[i].Line_data[j].Line_data_occ.length; l++) {
              const cell = this.f4797FinalData[i].Line_data[j].Line_data_occ[l];
              if (cell.IS_EDITABLE_CELL == "Y" && 
                  cell.DATA_TYPE == "dateVarious" && 
                  cell.EDIT_TYPE == "custom_date_various" &&
                  lineType == "NEW_EDIT_ROW") {
                cell.ATTRIB_VALUE = this.getDateFormat(cell.ATTRIB_VALUE);
              } else if (cell.DATA_TYPE == "number" && cell.EDIT_TYPE == "currency") {
                // Angular [(ngModel)] on text input produces strings; convert to number to match legacy behavior
                cell.ATTRIB_VALUE = parseInt(cell.ATTRIB_VALUE) || 0;
              } else if (cell.IS_EDITABLE_CELL == "Y" && 
                         cell.DATA_TYPE == "string" && 
                         cell.EDIT_TYPE == "checkbox" &&
                         lineType == "NEW_EDIT_ROW") {
                if (cell.ATTRIB_VALUE == null || cell.ATTRIB_VALUE == undefined) {
                  cell.ATTRIB_VALUE = "N";
                }
              }
            }
          }
        }
      }
    }
  }

  save() {
    if (this.isLocked) {
      this._snackBar.open('This entity is locked. Save is disabled.', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }
    this.isSaveClicked = true;
    this.crudLoading = true;
    
    // Create copy for processing
    this.f4797FinalData = _.cloneDeep(this.f4797FormData);
    
    if (_.isEqual(this.f4797FormData, this.f4797OriginalData)) {
      this._snackBar.open('No changes to save', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      this.isSaveClicked = false;
      this.crudLoading = false;
      this.cancel();
      return;
    }
    
    // Update form data values (convert dates, set checkbox defaults) before validation
    this.updateFormDataValues();
    
    const returnValue = this.validateFormValues();
    if (returnValue == 1) {
      this._snackBar.open('Please fix validation errors before saving', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      this.isSaveClicked = false;
      this.crudLoading = false;
      return;
    }
    
    if (this.unAllocatedAmt != 0) {
      if (!confirm("Allocated Amount is not fully adjusted among Gain or (Loss). Confirm to save the changes.")) {
        this.isSaveClicked = false;
        this.crudLoading = false;
        return;
      }
    }
    
    const finalData: any[] = [];
    
    for (let x = 0; x < this.f4797FinalData.length; x++) {
      if (this.f4797FinalData[x].ROW_TYPE == 'CH') {
        this.f4797FinalData[x].Line_data.splice(0, 2);
        if (this.f4797FinalData[x].Line_data.length != null && this.f4797FinalData[x].Line_data.length != undefined) {
          for (let j = 0; j < this.f4797FinalData[x].Line_data.length; j++) {
            this.f4797FinalData[x].Line_data[j].Line_data_occ[this.f4797FinalData[x].Line_data[j].Line_data_occ.length - 1] = 
              this.f4797FinalData[x].Line_data[j + 1].Line_data_occ[0];
            this.f4797FinalData[x].Line_data.splice(j + 1, 1);
            for (let k = 0; k < this.f4797FinalData[x].Line_data[j].Line_data_occ.length; k++) {
              finalData.push(this.f4797FinalData[x].Line_data[j].Line_data_occ[k]);
            }
          }
        }
        if (this.f4797FinalData[x].DELETE_ROWS.length != null && this.f4797FinalData[x].DELETE_ROWS.length != undefined) {
          for (let j = 0; j < this.f4797FinalData[x].DELETE_ROWS.length; j++) {
            for (let k = 0; k < this.f4797FinalData[x].DELETE_ROWS[j].Line_data_occ.length; k++) {
              this.f4797FinalData[x].DELETE_ROWS[j].Line_data_occ[k].IS_CHANGED_FLAG = "D";
              finalData.push(this.f4797FinalData[x].DELETE_ROWS[j].Line_data_occ[k]);
            }
          }
        }
      }
      if (this.f4797FinalData[x].ROW_TYPE == 'TH' || this.f4797FinalData[x].ROW_TYPE == 'DT') {
        for (let j = 0; j < this.f4797FinalData[x].Line_data.length; j++) {
          finalData.push(this.f4797FinalData[x].Line_data[j]);
        }
      }
    }
    
    const clob_settings = [{
      sso_id: this.logged_in_user, 
      screen_key: this.globalService?.globalParams?.screen_key || ''
    }];
    
    this.f4797Service.saveForm4797(
      this.GENERAL_CONFIG.base_url,
      finalData,
      clob_settings,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        
        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Form has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.markForCheck();
      }
    );
  }

  reset() {
    this.f4797FormData = _.cloneDeep(this.f4797OriginalData);
    this.getUnallocatedAmt();
  }

  cancel() {
    if (_.isEqual(this.f4797FormData, this.f4797OriginalData)) {
      this.requestGridUpdate.emit('cancel');
    } else {
      if (this.crudLoading) {
        this._snackBar.open('The transaction is still in progress', 'Close', {
          duration: 4000,
          panelClass: ['info-snackbar']
        });
      } else if (confirm("Sales of Business Property Information is not save. Do you want to close Form without saving changes?")) {
        this.requestGridUpdate.emit('cancel');
      }
    }
  }

  // Track by function for ngFor
  trackByIndex(index: number): number {
    return index;
  }

  // Handle date change event
  onDateChange(event: Event, mainIndex: number, occurrence: number, lineNo: string, colIndex: number, column: any) {
    const target = event.target as HTMLInputElement;
    column.ATTRIB_VALUE = target.value;
    this.valueChanged(mainIndex, occurrence, lineNo, colIndex);
  }

  // Handle checkbox change for column checkbox (matches f-4562 pattern)
  onCheckboxChangeColumn(column: any, mainIndex: number, occurrence: any, lineNo: string, colIndex: number, event: any) {
    column.ATTRIB_VALUE = event.target.checked ? 'Y' : 'N';
    this.valueChanged(mainIndex, occurrence, lineNo, colIndex);
  }

  // Handle checkbox change for VARIOUS date option (matches f-4562 pattern)
  onDateVariousCheckboxChange(column: any, mainIndex: number, occurrence: any, lineNo: string, colIndex: number, event: any) {
    const isChecked = event.target.checked;
    
    if (isChecked) {
      // Store the current date value before setting to VARIOUS (so we can restore it later)
      if (column.ATTRIB_VALUE && column.ATTRIB_VALUE !== 'VARIOUS') {
        column._previousDateValue = column.ATTRIB_VALUE;
      }
      // Set to "VARIOUS" when checkbox is checked
      column.ATTRIB_VALUE = 'VARIOUS';
    } else {
      // Restore the previous date value if present, otherwise use current date
      if (column._previousDateValue) {
        column.ATTRIB_VALUE = column._previousDateValue;
      } else {
        // Default to current date in YYYY-MM-DD format when checkbox is unchecked
        column.ATTRIB_VALUE = this.convertToHtmlDateFormat(new Date());
      }
    }
    
    this.valueChanged(mainIndex, occurrence, lineNo, colIndex);
  }

  // Convert date to HTML date input format (YYYY-MM-DD)
  convertToHtmlDateFormat(date: any): string {
    if (!date) return '';
    
    let d: Date;
    if (typeof date === 'string') {
      d = new Date(date);
    } else if (date instanceof Date) {
      d = date;
    } else {
      return '';
    }
    
    if (isNaN(d.getTime())) return '';
    
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }
}

